import type { CSSProperties, ReactNode, RefObject } from 'react'
import { createRef, useCallback, useEffect, useRef, useState } from 'react'
import { useCoachingStore } from '../store'
import {
  GUTTER_CARD_WIDTH,
  type GutterItem,
  gutterLeft,
  type PlacedItem,
} from './_lib/gutter-layout'
import { BehavioralReview } from './behavioral-review'
import { CodingReview } from './coding-review'
import { CommentCard, type CommentCardLayout } from './comment-card'
import { Composer } from './composer'
import type {
  ReviewComment,
  ReviewSubmission,
  ReviewType,
  SubmittedReviewRow,
} from './contracts'
import { OverallComment } from './overall-comment'
import { RankingBanner } from './ranking-banner'
import { ReviewQueue } from './review-queue'
import { PacingSection } from './sections/pacing-section'
import { PostMortemSection } from './sections/post-mortem-section'
import { SystemDesignReview } from './system-design-review'
import { TaskReview } from './task-review'
import { type GutterSection, useGutterLayout } from './use-gutter-layout'

/**
 * Review tab (spec §8) — replaces the F2 placeholder. Owns:
 * - the center column (`max-width:780px`) inside the already-1460px-wide
 *   dashboard shell (spec §8.1) — `CoachingDashboardShell` (../index.tsx)
 *   already constrains + centers that outer width, so this component only
 *   needs its own `position:relative` wrapper for the gutter math to key
 *   off of;
 * - the gutter overlay: one `useGutterLayout` call over every section key
 *   the expanded submission's comments (or the open composer) reference,
 *   rendering `commentsFor(expandedId)` + the floating `Composer` at
 *   `layoutGutter` positions;
 * - the per-type body slot: `BODY_BY_TYPE` maps `ReviewType -> body
 *   component`. A5/A6/A7 each add one import + one map entry (their own
 *   `coding-review.tsx` / `system-design-review.tsx` /
 *   `behavioral-review.tsx`) — same file, same lane, no merge conflict.
 *   Body components receive `getSectionRef` and can anchor ANY section key
 *   (`getSectionRef('code')`, `getSectionRef('nfr')`, …) — no shared
 *   plumbing here needs editing per type. That map is complete as of LG-905,
 *   so nothing routes through `DefaultReviewBody` any more — it renders only
 *   the sections A4 owns (spec §8.4: ranking banner, pacing, post-mortem,
 *   overall comment) and survives as the `??` runtime backstop below.
 */

interface ReviewBodyProps {
  /**
   * The review-backed arm only: `renderBody` branches the practice rows off
   * before any body component runs, so a body may read lifecycle and
   * artifact fields without a guard.
   */
  submission: SubmittedReviewRow
  /**
   * Stable per-key section anchor refs (lazily created, identity preserved
   * across renders). Attach `getSectionRef(key)` to the DOM element of any
   * section whose key appears in `ReviewComment.section` — the gutter
   * engine anchors that section's comments to it. Keys are free-form
   * strings; a section that is not currently mounted is simply skipped by
   * the layout hook (its comments don't float until the section exists).
   */
  getSectionRef: (key: string) => RefObject<HTMLDivElement | null>
}

type ReviewBodyComponent = (props: ReviewBodyProps) => ReactNode

// Total, not `Partial` (LG-905): `task: TaskReview` was the last entry the
// A5/A6/A7 plan above was waiting on, so every `ReviewType` now has a body of
// its own and nothing needs `DefaultReviewBody` to stand in. A fifth type
// falling through to it would render `PacingSection` over a row that may have
// no duration to show — a ticked card's is 0 by construction — so a new type
// is a build error here until someone decides what its body is. The `??` at
// the render site stays as a runtime backstop for a `type` no map covers.
const BODY_BY_TYPE: Record<ReviewType, ReviewBodyComponent> = {
  coding: CodingReview,
  sd: SystemDesignReview,
  behavioral: BehavioralReview,
  task: TaskReview,
}

function DefaultReviewBody(props: ReviewBodyProps): ReactNode {
  const { submission, getSectionRef } = props
  const { commentsFor } = useCoachingStore().review
  const comments = commentsFor(submission.id)

  return (
    <>
      <RankingBanner ranking={submission.ranking} />
      <div className="flex flex-col gap-3.5">
        <PacingSection
          pacing={submission.pacing}
          comments={comments}
          anchorRef={getSectionRef('pacing')}
        />
        <PostMortemSection
          postMortem={submission.postMortem}
          comments={comments}
          anchorRef={getSectionRef('postmortem')}
        />
        <div ref={getSectionRef('overall')}>
          <OverallComment submissionId={submission.id} />
        </div>
      </div>
    </>
  )
}

function toGutterItem(
  comment: ReviewComment,
  layout: CommentCardLayout | undefined,
): Omit<GutterItem, 'anchorY'> {
  return {
    id: comment.id,
    kind: 'comment',
    bodyLen: comment.body.length,
    // `pending` is the layout engine's name for "this card carries an action
    // row" (+42px), not for the stored status: an AI draft's
    // approve/rewrite/dismiss row, or the save row an unsaved edit adds. A
    // coach draft on its own carries neither (LG-641).
    pending:
      (comment.status === 'pending' && comment.source === 'ai') ||
      comment.edited === true,
    // Diff-mode / rewrite-panel state lives inside each CommentCard; the
    // card reports it up via onLayoutChange so the spec §8.1 height bumps
    // (+170/+130 open diff, +100 rewrite panel) feed the estimate. Before
    // the card's first report, mirror its initial render: a diff renders
    // open in inline mode by default, the rewrite panel closed.
    openDiff: layout ? layout.openDiff : comment.diff ? 'inline' : null,
    rewriteOpen: layout?.rewriteOpen ?? false,
  }
}

const COMPOSER_ITEM_ID = '__composer__'
const COMPOSER_GUTTER_ITEM: Omit<GutterItem, 'anchorY'> = {
  id: COMPOSER_ITEM_ID,
  kind: 'composer',
  bodyLen: 0,
  pending: false,
  openDiff: null,
  rewriteOpen: false,
}

function placedStyle(p: PlacedItem): CSSProperties {
  return {
    position: 'absolute',
    top: p.top,
    left: gutterLeft(p.side),
    width: GUTTER_CARD_WIDTH,
  }
}

// Where an open composer goes when the gutter engine has never placed it —
// possible only if its anchor section was already unmounted at open time,
// which no current opener can do (they all live inside their section).
const COMPOSER_FALLBACK_STYLE: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: gutterLeft('right'),
  width: GUTTER_CARD_WIDTH,
}

export function ReviewTab(): ReactNode {
  const { review } = useCoachingStore()
  const { expandedId, commentsFor, comments, composer, closeComposer } = review

  // Lazily-created, identity-stable anchor refs keyed by free-form section
  // string — the registry the per-type bodies anchor arbitrary sections
  // through. Entries for unmounted sections are harmless (the layout hook
  // skips refs with no current element).
  const sectionRefs = useRef(
    new Map<string, RefObject<HTMLDivElement | null>>(),
  )
  const getSectionRef = useCallback(
    (key: string): RefObject<HTMLDivElement | null> => {
      let ref = sectionRefs.current.get(key)
      if (!ref) {
        ref = createRef<HTMLDivElement>()
        sectionRefs.current.set(key, ref)
      }
      return ref
    },
    [],
  )

  // Per-card layout state (open diff mode / rewrite panel) reported up by
  // CommentCard so the gutter height estimates track it (spec §8.1).
  // Functional update bails out (returns `prev`) when nothing changed, so
  // the card's report-on-mount effect can't render-loop.
  const [cardLayouts, setCardLayouts] = useState<
    Record<string, CommentCardLayout>
  >({})
  const handleCardLayoutChange = useCallback(
    (id: string, layout: CommentCardLayout) => {
      setCardLayouts((prev) => {
        const current = prev[id]
        if (
          current &&
          current.openDiff === layout.openDiff &&
          current.rewriteOpen === layout.rewriteOpen
        ) {
          return prev
        }
        return { ...prev, [id]: layout }
      })
    },
    [],
  )

  // CRITICAL guard: the floating composer lives OUTSIDE the keyed expanded
  // subtree (it's positioned in the gutter overlay), so switching the
  // accordion would otherwise carry an open composer — draft and all —
  // over to the new submission, and `post()` targets `expandedId`, landing
  // the comment on the wrong submission. Close it on every switch.
  //
  // Which is a switch is read off the last submission actually on screen, not
  // off every change of `expandedId`: `expandedId` also goes null when the
  // coach folds the week the review sits in (`useReviewUiState`), and folding
  // is navigation, not abandonment (LG-642). Keyed on the raw value this would
  // close the composer on the way out — and again on the way back in, when the
  // id returns non-null — costing the coach a draft they never dismissed. A
  // null spell is therefore skipped, and the composer only closes when a
  // DIFFERENT submission takes the slot.
  const lastOnScreenId = useRef<string | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `closeComposer` is a per-render dispatch wrapper with stable behavior — including it would re-run (and re-close) every render; only an actual submission switch should close the composer.
  useEffect(() => {
    if (expandedId === null) return
    if (
      lastOnScreenId.current !== null &&
      lastOnScreenId.current !== expandedId
    )
      closeComposer()
    lastOnScreenId.current = expandedId
  }, [expandedId])

  const expandedComments = expandedId ? commentsFor(expandedId) : []

  // Group gutter items per section key: every distinct `comment.section`
  // among the expanded submission's comments, plus the open composer's
  // section. Keys are free-form — whatever sections A5/A6/A7's bodies
  // anchor via getSectionRef participate automatically; sections with no
  // mounted element are skipped by the hook.
  const sectionKeys: string[] = []
  for (const c of expandedComments) {
    if (!sectionKeys.includes(c.section)) sectionKeys.push(c.section)
  }
  if (composer && !sectionKeys.includes(composer.section)) {
    sectionKeys.push(composer.section)
  }

  const gutterSections: GutterSection[] = sectionKeys.map((key) => ({
    ref: getSectionRef(key),
    items: [
      ...expandedComments
        .filter((c) => c.section === key)
        .map((c) => toGutterItem(c, cardLayouts[c.id])),
      ...(composer?.section === key ? [COMPOSER_GUTTER_ITEM] : []),
    ],
  }))

  const { placed, containerRef } = useGutterLayout(
    gutterSections,
    // Deps contract (see use-gutter-layout.ts): `expandedId` toggles which
    // section refs are mounted; `comments` is the slice's memoized flat
    // array (stable unless a real mutation happened — `commentsFor()`
    // returns a fresh array each call and must NOT go in deps);
    // `composer` adds/moves the composer item; `cardLayouts` changes item
    // height estimates.
    [expandedId, comments, composer, cardLayouts],
  )

  // WHETHER the composer exists is the store's call alone; the gutter engine
  // only ever decides WHERE (LG-828). A section can unmount under an open
  // composer for reasons no mount rule can enumerate — e.g. dismissing the
  // section's last comment while its content is null — and gating the
  // composer's render on `placed` silently destroyed it while the store kept
  // reporting it open. When the anchor is gone the composer holds its last
  // placed position (a ref, so the value survives the placement's absence)
  // instead of unmounting.
  const composerPlacement = placed.find((p) => p.id === COMPOSER_ITEM_ID)
  const lastComposerStyle = useRef<CSSProperties | null>(null)
  useEffect(() => {
    if (composerPlacement)
      lastComposerStyle.current = placedStyle(composerPlacement)
  }, [composerPlacement])

  function renderBody(submission: ReviewSubmission): ReactNode {
    // A session nobody submitted is read-only (LG-646): there is no review to
    // hang comments or lifecycle on, so the body is the post-mortem alone —
    // no composer wiring, no gutter anchors, and the section itself renders
    // nothing when the client never wrote one.
    if (submission.notSubmitted === true) {
      return <PostMortemSection postMortem={submission.postMortem} readOnly />
    }
    const Body = BODY_BY_TYPE[submission.type] ?? DefaultReviewBody
    return <Body submission={submission} getSectionRef={getSectionRef} />
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="mx-auto flex max-w-[780px] flex-col gap-3">
        <ReviewQueue renderExpanded={renderBody} />
      </div>

      {placed.map((p) => {
        if (p.id === COMPOSER_ITEM_ID) return null
        const comment = expandedComments.find((c) => c.id === p.id)
        return comment ? (
          <CommentCard
            key={p.id}
            comment={comment}
            style={placedStyle(p)}
            onLayoutChange={handleCardLayoutChange}
          />
        ) : null
      })}

      {/* `expandedId !== null` keeps LG-642's fold behavior: folding the week
          hides the composer without ending its session, and the accordion
          switch above already closes it when a DIFFERENT submission expands. */}
      {composer && expandedId !== null && (
        <Composer
          style={
            composerPlacement
              ? placedStyle(composerPlacement)
              : (lastComposerStyle.current ?? COMPOSER_FALLBACK_STYLE)
          }
        />
      )}
    </div>
  )
}

// Re-exported so A5/A6/A7's per-type body files can import the shared prop
// shape from here without duplicating it.
export type { ReviewBodyProps }
